import { useTheme } from '@emotion/react';
import { themeGet } from '@styled-system/theme-get';
import {
  useComposite,
  CompositeOptions,
  CompositeHTMLProps,
  usePopover,
  PopoverOptions,
  PopoverHTMLProps,
  PopoverStateReturn,
} from 'reakit';
import { createComponent, createHook } from 'reakit-system';

import { useBox, BoxOptions, BoxHTMLProps } from '../../Box/Box';
import { useStyles } from '../../hooks/useStyles';
import useScrollStyles from '../../utils/useScrollStyles';
import pxToRem from '../../utils/pxToRem';

type ListboxOptions = BoxOptions &
  CompositeOptions &
  PopoverOptions &
  Pick<PopoverStateReturn, 'unstable_referenceRef'>;
type ListboxHTMLProps = BoxHTMLProps & CompositeHTMLProps & PopoverHTMLProps;

const useListbox = createHook<ListboxOptions, ListboxHTMLProps>({
  name: 'Listbox',
  compose: [useBox, useStyles, useComposite, usePopover],
  useOptions(options) {
    const theme = useTheme();
    const transform = options.unstable_popoverStyles?.transform;
    const ariaControls = options.unstable_referenceRef.current?.attributes.getNamedItem(
      'aria-controls'
    );
    if (ariaControls?.value) {
      const [firstValue] = ariaControls.value.split(' ');
      ariaControls.value = firstValue;
    }
    return {
      ...options,
      unstable_autoFocusOnShow: false,
      unstable_autoFocusOnHide: false,
      unstable_popoverStyles: {
        /* eslint-disable @typescript-eslint/no-unsafe-assignment -- не наши типы */
        ...options.unstable_popoverStyles,
        border: themeGet('borders.2')({ theme }),
        borderColor: themeGet('colors.popoverBorderColor')({ theme }),
        borderRadius: themeGet('radii.popover')({ theme }),
        boxShadow: themeGet('shadows.popover')({ theme }),
        color: themeGet('colors.gray-90')({ theme }),
        maxHeight: pxToRem(228),
        maxWidth: themeGet('sizes.semi-wide')({ theme }),
        width: '100%',
        overflowY: 'auto',
        paddingTop: themeGet('space.comboboxListPadding')({ theme }),
        paddingBottom: themeGet('space.comboboxListPadding')({ theme }),
        paddingLeft: 0,
        paddingRight: 0,
        background: themeGet('colors.white')({ theme }),
        zIndex: 100,
        outline: 'none',
        /* eslint-enable @typescript-eslint/no-unsafe-assignment -- продолжаем проверку */
        // Баг на мобилке: ломается верстка потому что изначано при рендере поповера производится сдвиг по горизонтали
        // Убирает сдвиг по горизонтали
        transform: transform?.replace(/(translate3d\()\d+(.*)/, '$10$2'),
      },
    };
  },
  useComposeProps(options, props) {
    const scrollStylesClassname = useScrollStyles();

    let newProps = { ...props };

    newProps = useComposite(options, newProps);
    newProps = usePopover(options, newProps);
    return {
      ...newProps,
      tabIndex: undefined,
      className: scrollStylesClassname,
      role: 'listbox',
    };
  },
});

export const Listbox = createComponent({
  as: 'div',
  useHook: useListbox,
  memo: true,
});
